import type { ImageLoadedListener } from '..';
import {
  NARROW_PROBLEM_WIDTH,
  PROBLEM_WIDTH,
} from '../problemCanvas/problemCanvasConstants';
import { ProblemJSDependencies } from '../problemJSDependencies';
import {
  getReadAloudWrapper,
  injectReadAloudButton,
} from '../readAloud/readAloudUtil';
import type { HighlightLocation, TTSDTO } from '../readAloud/TTS';
import { READ_ALOUD_HIGHLIGHT_COLOR_OVERLAY } from '../state';
import { base64ToBlob } from '../utils/base64';
import { drawImage } from '../utils/drawImage';
import { getImage } from '../utils/getImage';

import { BasicObject, MATH_IMAGE_GMM_NAME } from './basicObject';

const MAX_LOADING_WHEEL_SIZE = 50;

interface ImageDTO {
  w: number;
  h: number;
  // Store reference to built image so we don't need to
  // reload/rebuild on subsequent calls to setProblem,
  // such as when student clicks on other problem, then
  // clicks back to this problem.
  image?: HTMLImageElement;
  svg?: string;
  base64?: string;
}

interface MathImageDependencies {
  paintCanvas: () => void;
}

export class MathImage extends BasicObject {
  // helps with debugging
  gmmName = MATH_IMAGE_GMM_NAME;

  image: HTMLImageElement;

  dto: ImageDTO;

  loaded = false;

  problemJS: MathImageDependencies;

  // Images provided by DynamicColumnTables sometimes support read aloud with highlighted locations
  highlightLocations: HighlightLocation[] | undefined;
  highlightIndex = -1;

  constructor(
    dto: ImageDTO,
    problemJS: MathImageDependencies,
    x?: number,
    y?: number
  ) {
    super(x, y);

    this.problemJS = problemJS;
    this.dto = dto;

    this.setAllDim(dto.w, dto.h);

    if (dto.image) {
      this.image = dto.image;
      this.loaded = true;
    } else this.image = new Image(this.viewportW, this.viewportH);
  }

  // Enable read aloud for this image. Built to support DynamicColumnTable.
  setTTS(tts: TTSDTO): void {
    this.highlightLocations = tts.highlightLocations;
  }

  loadImageHelper(
    delay?: boolean,
    imageLoadedListener?: ImageLoadedListener
  ): void {
    if (imageLoadedListener) {
      imageLoadedListener.loadingImage();
      // permit capture of canvas image as dataURL (for internal testing)
      this.image.crossOrigin = 'Anonymous';
    }

    // set up the listener before actually loading the image by setting src
    this.image.onload = () => {
      this.loaded = true;

      // Save image to student app problem store for later re-use without needing to rebuild
      this.dto.image = this.image;

      if (delay) {
        // Delay canvas paint for Safari to finish loading embedded fonts for svg
        window.setTimeout(this.problemJS.paintCanvas, 100);
      } else {
        this.problemJS.paintCanvas();
      }

      imageLoadedListener?.loadedImage();
    };
  }

  loadBase64Image(
    base64: string,
    imageLoadedListener?: ImageLoadedListener
  ): void {
    if (this.loaded) return;

    this.loadImageHelper(false, imageLoadedListener);

    base64ToBlob(base64).then(blob => {
      this.image.src = URL.createObjectURL(blob);
    });
  }

  paintMe(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    if (this.loaded) {
      drawImage(ctx, this.image, 0, 0, this.image.width, this.image.height);
    } else {
      let side = Math.min(this.viewportW, this.viewportH);

      side = Math.min(side, MAX_LOADING_WHEEL_SIZE);

      drawImage(
        ctx,
        getImage('loadingwheel', this.problemJS.paintCanvas),
        this.viewportW / 2 - side / 2,
        this.viewportH / 2 - side / 2,
        side,
        side
      );
    }

    ctx.restore();
  }

  isTTS(): boolean {
    return !!this.highlightLocations;
  }

  getPaintedCharCount(): number {
    return this.highlightLocations?.length || 0;
  }

  setHighlightReadAloud(start: number): void {
    this.highlightIndex = start;
  }

  clearHighlightReadAloud(): void {
    this.highlightIndex = -1;
  }

  /**
   * BasicObject sets scale in paintPre, if needed, and our
   * invokation of super.paintPost here will reset ctx scale.
   * @param ctx
   */
  paintPost(ctx: CanvasRenderingContext2D): void {
    if (this.isTTS()) {
      ctx.save();

      // Scaled by BasicObject.paintPre if we've called BasicObject.setMaxWidth
      this.paintHighlight(ctx);

      ctx.restore();
    }

    super.paintPost(ctx);
  }

  // During read aloud, paint a border around the highlighted zone and a semi-transparent
  // overlay over the highlighted zone. The overlay is necessary because
  // many DCTs have a white background, so we cannot simply paint a solid highlighted
  // rectangle behind the zone (which would work if DCT's and their cells
  // always used transparent backgrounds).
  paintHighlight(ctx: CanvasRenderingContext2D): void {
    if (this.highlightIndex == -1 || !this.highlightLocations) return;

    const highlightLocation = this.highlightLocations[this.highlightIndex];

    ctx.save();

    // rectangle overlay
    ctx.fillStyle = READ_ALOUD_HIGHLIGHT_COLOR_OVERLAY;
    ctx.fillRect(
      highlightLocation.x,
      highlightLocation.y,
      highlightLocation.w,
      highlightLocation.h
    );

    ctx.restore();
  }
}

export function getProbImage(
  problemJS: ProblemJSDependencies,
  args: any,
  maxWidth?: number,
  x = 0,
  y = 0,
  targetForReadAloud?: any
): BasicObject {
  const imageData = args.image;

  // build our image holder prior to loading the image in order
  // to be ready handle 'onload' event
  const ret = new MathImage(imageData, problemJS, x, y);

  if (imageData.base64) {
    ret.loadBase64Image(imageData.base64, problemJS.imageLoadedListener);

    // Don't save raw base64 image data on student app because we save the Image created from it.
    // If we keep the base64, we're doubling (plus some) memory usage.
    // Avoid any race conditions by only setting base64 to undefined after the image has loaded.
    if (problemJS.isStudent && ret.loaded) imageData.base64 = undefined;
  }

  if (maxWidth) {
    ret.setMaxWidth(maxWidth);
  } else {
    ret.setMaxWidth(
      problemJS.isNarrow() ? NARROW_PROBLEM_WIDTH : PROBLEM_WIDTH
    );
  }

  if (!problemJS.isReadAloud() || !args.tts) return ret;

  ret.setTTS(args.tts);

  // If the caller supplied a targetForReadAloud, it means we already have a BasicObject
  // that should show the audio icon. Per pattern in getStaticPanel, we inject the tts
  // from the new panel into the tts of the targetForReadAloud and then return the new
  // panel. It is the caller's responsibility to shrink the returned panel if needed.
  if (targetForReadAloud) {
    injectReadAloudButton(
      targetForReadAloud,
      args.tts,
      {
        paintCanvas: problemJS.paintCanvas,
        errorToServer: problemJS.errorToServer,
        $canvas: problemJS.$canvas,
      },
      ret
    );

    return ret;
  }

  // ReadAloudWrapper protects the size of the audio icon when
  // the image needs to be scaled down.
  const readAloudWrapper = getReadAloudWrapper();

  readAloudWrapper.add(ret);
  injectReadAloudButton(
    readAloudWrapper,
    args.tts,
    {
      paintCanvas: problemJS.paintCanvas,
      errorToServer: problemJS.errorToServer,
      $canvas: problemJS.$canvas,
    },
    ret
  );
  readAloudWrapper.layoutChildrenLR();
  // Shrink to fit available space, audio icon sizing is protected by ReadAloudWrapper
  readAloudWrapper.setMaxWidth(
    problemJS.isNarrow() ? NARROW_PROBLEM_WIDTH : PROBLEM_WIDTH
  );

  return readAloudWrapper;
}
